import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const CustomerEngagementChart = ({ data = [] }) => {
  const COLORS = [
    'var(--color-primary)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-error)',
    'var(--color-secondary)'
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0];
      return (
        <div className="bg-popover border border-border rounded-lg shadow-moderate p-3">
          <p className="text-sm font-medium text-foreground">{data?.name}</p>
          <p className="text-sm text-muted-foreground">
            Count: <span className="font-medium text-foreground">{data?.value}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Percentage: <span className="font-medium text-foreground">{((data?.value / data?.payload?.total) * 100)?.toFixed(1)}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload?.map((entry, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry?.color }}
            />
            <span className="text-sm text-muted-foreground">{entry?.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const totalCustomers = data?.reduce((sum, item) => sum + item?.value, 0);
  const dataWithTotal = data?.map(item => ({ ...item, total: totalCustomers }));

  const engagementMetrics = [
    {
      label: 'Active Customers',
      value: data?.find(d => d?.name === 'Active')?.value || 0,
      icon: 'Users',
      color: 'text-success'
    },
    {
      label: 'New This Month',
      value: data?.find(d => d?.name === 'New')?.value || 0,
      icon: 'UserPlus',
      color: 'text-primary'
    },
    {
      label: 'Repeat Customers',
      value: data?.find(d => d?.name === 'Repeat')?.value || 0,
      icon: 'Repeat',
      color: 'text-warning'
    },
    {
      label: 'At Risk',
      value: data?.find(d => d?.name === 'At Risk')?.value || 0,
      icon: 'AlertTriangle',
      color: 'text-error'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Customer Engagement Analytics</h2>
        <div className="flex items-center space-x-2">
          <button className="px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-md">
            This Month
          </button>
          <button className="px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-smooth">
            Last Month
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metrics Cards */}
        <div className="space-y-4">
          {engagementMetrics?.map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-background border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 bg-muted rounded-lg flex items-center justify-center`}>
                  <Icon name={metric?.icon} size={16} className={metric?.color} />
                </div>
                <span className="text-sm font-medium text-foreground">{metric?.label}</span>
              </div>
              <span className="text-lg font-bold text-foreground">{metric?.value}</span>
            </div>
          ))}
          
          <div className="p-4 bg-background border border-border rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Total Customers</span>
              <span className="text-xl font-bold text-primary">{totalCustomers}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Across all engagement categories
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="flex flex-col">
          <div className="flex-1" style={{ minHeight: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dataWithTotal}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {dataWithTotal?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Insights */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-medium text-foreground mb-3">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium">Customer Retention</p>
              <p className="text-xs text-muted-foreground">
                {((data?.find(d => d?.name === 'Repeat')?.value || 0) / totalCustomers * 100)?.toFixed(1)}% of customers are repeat buyers
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Target" size={16} className="text-primary mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium">Growth Opportunity</p>
              <p className="text-xs text-muted-foreground">
                Focus on converting {data?.find(d => d?.name === 'At Risk')?.value || 0} at-risk customers
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerEngagementChart;